/**
 * 領域べつの「さいきんの とりくみ量」(docs/14_characters.md §2 パルの色システム)。
 *
 * パルの体色は**累計ではなく直近数週間の比率**で決まる(サボると白へ戻る)。
 * そのために必要なのは「どの領域を、いつ、どれだけ遊んだか」だけなので、
 * ここには**日 × 領域のセッション数**しか置かない(1セッション = リザルト1回)。
 *
 * ## 置き場所と大きさ
 * - localStorage(`paltyping.activity.v1`)。**サーバーAPIは増やさない**
 * - 直近 `ACTIVITY_WINDOW_DAYS` 日ぶんだけ保持し、古い日は書き込みのたびに捨てる
 *   = 容量が一定(28日 × 7領域 = 最大でも数百バイト)
 * - `totals` だけは窓の外に出ても消えない累計(ステータス画面の「これまで」用)
 *
 * ## 領域(= 通貨キー)
 * 教科 → 領域の対応は**コインと同じ**(`services/wallet.ts` の `CURRENCY_BY_SUBJECT`)。
 * 表を二重に持つと必ずずれるので、教科からの変換はそちらを引く。
 * `trophy`(チャレンジ)は「取り組み領域」ではないので含めない。
 */

import { localDateKey, shiftDateKey } from './learning';
import type { KeyValueStorage } from './progress';
import { appStorage, recordUserStateOperation, registerUserStateReload } from './userState';
import { CURRENCY_BY_SUBJECT, PUZZLE_CURRENCY, TYPING_CURRENCY } from './wallet';

/**
 * 取り組みの領域。**通貨キーと同じ文字列**(docs/13 §9.1 の普遍領域)。
 * 並びはステータス画面・レインボーの色順にそのまま使う。
 */
export const ACTIVITY_DOMAINS = [
  'typing',
  'puzzle',
  'math',
  'language',
  'science',
  'social',
  'life',
] as const;

export type ActivityDomain = (typeof ACTIVITY_DOMAINS)[number];

/** 色に反映する期間(日)。「直近数週間」= 4週間(docs/14 §2)。 */
export const ACTIVITY_WINDOW_DAYS = 28;

/** 日 → 領域べつのセッション数(0 の領域はキーごと持たない)。 */
export type ActivityDayCounts = Readonly<Partial<Record<ActivityDomain, number>>>;

export interface ActivityLog {
  readonly version: 1;
  /** `YYYY-MM-DD`(ローカル日付)→ その日の領域べつセッション数。直近28日ぶんだけ。 */
  readonly days: Readonly<Record<string, ActivityDayCounts>>;
  /** 領域べつの累計セッション数(窓から出ても消えない)。 */
  readonly totals: ActivityDayCounts;
}

export const EMPTY_ACTIVITY_LOG: ActivityLog = { version: 1, days: {}, totals: {} };

const STORAGE_KEY = 'paltyping.activity.v1';

/** 1セッションで足せる上限(壊れた呼び出しで数字が暴れないように)。 */
const MAX_COUNT_PER_CALL = 100;

// ── 参照ヘルパ(純関数) ─────────────────────────────────

function isDomain(value: unknown): value is ActivityDomain {
  return typeof value === 'string' && (ACTIVITY_DOMAINS as readonly string[]).includes(value);
}

/**
 * まなびの教科 → 領域。知らない教科は `null`(記録しないだけ = 画面は壊れない)。
 * 「ちえ」がパズルと同じ領域になるのはコインと同じ扱い(docs/13 §9.1)。
 */
export function domainForSubject(subject: string): ActivityDomain | null {
  const currency = CURRENCY_BY_SUBJECT[subject];
  return isDomain(currency) ? currency : null;
}

/** タイピングの領域(呼び出し側が文字列リテラルを書かなくて済むように)。 */
export const TYPING_DOMAIN: ActivityDomain = TYPING_CURRENCY;
/** パズルのもりの領域。 */
export const PUZZLE_DOMAIN: ActivityDomain = PUZZLE_CURRENCY;

/** 窓に入る日付キー(新しい順)。 */
function windowDateKeys(nowMs: number): string[] {
  const today = localDateKey(nowMs);
  const keys: string[] = [today];
  for (let i = 1; i < ACTIVITY_WINDOW_DAYS; i += 1) keys.push(shiftDateKey(today, -i));
  return keys;
}

/**
 * 直近 `ACTIVITY_WINDOW_DAYS` 日の領域べつセッション数。
 * **色ロジックの唯一の入力**(`logic/palColor.ts`)。
 */
export function activityWindow(log: ActivityLog, nowMs: number = Date.now()): ActivityDayCounts {
  const counts: Partial<Record<ActivityDomain, number>> = {};
  for (const date of windowDateKeys(nowMs)) {
    const day = log.days[date];
    if (!day) continue;
    for (const domain of ACTIVITY_DOMAINS) {
      const value = day[domain];
      if (value) counts[domain] = (counts[domain] ?? 0) + value;
    }
  }
  return counts;
}

/** 領域べつの数を全部足す。 */
export function totalActivity(counts: ActivityDayCounts): number {
  let sum = 0;
  for (const domain of ACTIVITY_DOMAINS) sum += counts[domain] ?? 0;
  return sum;
}

/** 直近28日の日べつ合計(ステータス画面の「このごろ」のミニグラフ用。古い日 → 新しい日)。 */
export function dailyTotals(log: ActivityLog, nowMs: number = Date.now()): readonly number[] {
  return windowDateKeys(nowMs)
    .map((date) => totalActivity(log.days[date] ?? {}))
    .reverse();
}

// ── 更新(純関数) ───────────────────────────────────────

/**
 * 窓の外に出た**古い**日を捨てる。
 *
 * 未来の日付は残す(端末の時計がずれている / 日付をまたいで遊んでいる最中でも
 * 記録を消さないため)。日付キーは `YYYY-MM-DD` なので辞書順 = 時系列順。
 */
export function pruneActivity(log: ActivityLog, nowMs: number): ActivityLog {
  const oldest = shiftDateKey(localDateKey(nowMs), -(ACTIVITY_WINDOW_DAYS - 1));
  const days: Record<string, ActivityDayCounts> = {};
  for (const [date, counts] of Object.entries(log.days)) {
    if (date >= oldest) days[date] = counts;
  }
  return { ...log, days };
}

/**
 * 1セッションぶんを足した**新しい**ログを返す(古い日はここで捨てる)。
 * 記録は「回数」だけ。時間も点数も持たない(比率しか使わないため)。
 */
export function addActivity(
  log: ActivityLog,
  domain: ActivityDomain,
  at: number = Date.now(),
  count = 1,
): ActivityLog {
  const add = Math.min(MAX_COUNT_PER_CALL, Math.max(1, Math.trunc(count)));
  const pruned = pruneActivity(log, at);
  const date = localDateKey(at);
  const day = pruned.days[date] ?? {};
  return {
    version: 1,
    days: { ...pruned.days, [date]: { ...day, [domain]: (day[domain] ?? 0) + add } },
    totals: { ...pruned.totals, [domain]: (pruned.totals[domain] ?? 0) + add },
  };
}

// ── 保存形式 ─────────────────────────────────────────────

function parseCounts(raw: unknown): ActivityDayCounts {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const counts: Partial<Record<ActivityDomain, number>> = {};
  for (const [domain, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!isDomain(domain)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) continue;
    counts[domain] = Math.trunc(value);
  }
  return counts;
}

/** 壊れたJSON・未知のバージョンを捨てて空に落とす(他の progress 系と同じ方針)。 */
export function deserializeActivity(raw: string | null): ActivityLog {
  if (!raw) return EMPTY_ACTIVITY_LOG;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return EMPTY_ACTIVITY_LOG;
    }
    const record = parsed as Partial<ActivityLog>;
    if (record.version !== 1) return EMPTY_ACTIVITY_LOG;
    const days: Record<string, ActivityDayCounts> = {};
    for (const [date, value] of Object.entries(record.days ?? {})) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
      const counts = parseCounts(value);
      if (Object.keys(counts).length > 0) days[date] = counts;
    }
    return { version: 1, days, totals: parseCounts(record.totals) };
  } catch {
    return EMPTY_ACTIVITY_LOG;
  }
}

// ── 記録係 ───────────────────────────────────────────────

export interface ActivityRecorderOptions {
  readonly storage?: KeyValueStorage | null;
  readonly key?: string;
  readonly now?: () => number;
}

/**
 * リザルト画面から呼ばれる記録係。
 *
 * `dedupeKey` を渡すと、**同じキーでの2回目以降を無視**する。
 * React の StrictMode は開発時に effect を2回走らせるので、
 * これが無いと1回のリザルトで2セッション数えてしまう。
 */
export class ActivityRecorder {
  private log: ActivityLog | null = null;
  private readonly seen = new Set<string>();

  private readonly storage: KeyValueStorage | null;
  private readonly storageKey: string;
  private readonly now: () => number;

  constructor(options: ActivityRecorderOptions = {}) {
    this.storage = options.storage === undefined ? appStorage() : options.storage;
    this.storageKey = options.key ?? STORAGE_KEY;
    this.now = options.now ?? (() => Date.now());
  }

  load(): ActivityLog {
    this.log ??= deserializeActivity(this.storage?.getItem(this.storageKey) ?? null);
    return this.log;
  }

  /** 1セッションぶんを記録する。 */
  record(domain: ActivityDomain, options: { readonly dedupeKey?: string } = {}): ActivityLog {
    const { dedupeKey } = options;
    if (dedupeKey !== undefined) {
      if (this.seen.has(dedupeKey)) return this.load();
      this.seen.add(dedupeKey);
    }
    const at = this.now();
    this.log = addActivity(this.load(), domain, at);
    try {
      this.storage?.setItem(this.storageKey, JSON.stringify(this.log));
    } catch {
      // 容量超過・プライベートモード等。色が変わらないだけで遊べる。
    }
    recordUserStateOperation({
      ...(dedupeKey === undefined ? {} : { id: `activity:${dedupeKey}` }),
      section: 'activity',
      occurredAt: at,
      payload: { delta: { [localDateKey(at)]: { [domain]: 1 } } },
    });
    return this.log;
  }

  /** 次の `load()` で localStorage を読み直す(読み戻しのあと。docs/04 §5.1.1)。 */
  reload(): void {
    this.log = null;
  }

  reset(): ActivityLog {
    this.log = EMPTY_ACTIVITY_LOG;
    this.seen.clear();
    this.storage?.removeItem(this.storageKey);
    return this.log;
  }
}

/** アプリ全体で使う記録係。 */
export const activityRecorder = new ActivityRecorder();

// 読み戻し(docs/04 §5.1.1)のあと、サーバーのマージ結果を読み直せるようにする。
registerUserStateReload(() => activityRecorder.reload());
